/**
 * Shared admin bookings list — the single source of truth for the read-only
 * bookings table + filters, reused by BOTH surfaces:
 *
 *   1. The standalone `/bookings` page (`pages/bookings/index.tsx`) — renders it
 *      unlocked, with the full venue/status/date filter sidebar.
 *   2. The admin venue detail page Bookings tab (`AdminVenueDetailPage`) — renders
 *      it with `lockedVenueId` so the list is scoped to that one venue and the
 *      venue-filter input is hidden.
 *
 * Because both surfaces render this same component, a fix to the table, filters,
 * or fetch logic lands on both with zero duplication.
 *
 * Permission: callers must only mount this when the actor holds `bookings:read`
 * (super_admin + support + finance — content_moderator excluded per F16
 * Decision 13). The component does not gate itself; the route guard (standalone
 * page) or the tab gate (venue page) does.
 *
 * Read-only: every row deep-links to the F17 booking detail page at
 * `/bookings/:id` (the only place admins act on a booking — force-transition).
 *
 * Pagination note: `GET /admin/bookings` accepts a `cursor` but the service does
 * not yet apply it (keyset pagination is a documented follow-up), so this fetches
 * the most-recent `PAGE_LIMIT` rows and exposes status + date (+ venue, when
 * unlocked) filters to narrow older results. When the result is truncated we say
 * so rather than render a non-functional "next" control.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  type BadgeTone,
  Banner,
  Card,
  CurrencyDisplay,
  DateRangePicker,
  EmptyState,
  FormField,
  Select,
  Skeleton,
  Table,
  TextInput,
  type DateRange,
  type TableColumn,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import { BOOKING_STATUS_VALUES, type BookingStatus, type BookingSummary } from '@bravemove/types';
import { listAdminBookings } from '../../api/adminBookings';
import { translateApiError } from '../../auth/errors';

const PAGE_LIMIT = 100;

const STATUS_TONE: Record<BookingStatus, BadgeTone> = {
  pending: 'warning',
  confirmed: 'info',
  completed: 'success',
  cancelled: 'neutral',
  no_show: 'danger',
};

const STATUS_LABEL: Record<BookingStatus, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No-show',
};

function formatSlot(iso: string, timeZone: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone,
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function isBookingStatus(v: string): v is BookingStatus {
  return (BOOKING_STATUS_VALUES as readonly string[]).includes(v);
}

/**
 * BP-16 — the backend Zod schema requires `venueId` to be a UUID; anything
 * else 422s and blanks the whole list. Gate the filter client-side.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface AdminBookingsListProps {
  /**
   * When set, locks the list to one venue: every fetch is scoped to this venueId
   * and the venue-filter input is hidden (the venue is fixed by context). When
   * omitted, the venue-filter TextInput renders exactly as the standalone
   * `/bookings` page does today (URL-backed, BP-16 UUID-validated).
   */
  readonly lockedVenueId?: string;
}

export function AdminBookingsList({ lockedVenueId }: AdminBookingsListProps) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const isLocked = lockedVenueId != null;
  const venueIdParam = searchParams.get('venueId') ?? '';
  /** When locked the venue is fixed by the prop; otherwise it comes from the URL. */
  const effectiveVenueId = isLocked ? lockedVenueId : venueIdParam;
  const statusParamRaw = searchParams.get('status') ?? '';
  const statusParam = isBookingStatus(statusParamRaw) ? statusParamRaw : '';
  const dateFromParam = searchParams.get('dateFrom') ?? '';
  const dateToParam = searchParams.get('dateTo') ?? '';

  // Local-only venueId input (committed into the URL on blur / Enter). Unused
  // when locked (the venue-filter input is hidden), but kept defined so the
  // hooks order is stable across the locked/unlocked render paths.
  const [venueIdInput, setVenueIdInput] = useState(venueIdParam);
  const [venueIdError, setVenueIdError] = useState<string | null>(null);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setVenueIdInput(venueIdParam);
  }, [venueIdParam]);

  const [rows, setRows] = useState<readonly BookingSummary[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setRows([]);
    setError(null);
    try {
      const res = await listAdminBookings({
        ...(effectiveVenueId ? { venueId: effectiveVenueId } : {}),
        ...(statusParam ? { status: statusParam } : {}),
        ...(dateFromParam ? { dateFrom: dateFromParam } : {}),
        ...(dateToParam ? { dateTo: dateToParam } : {}),
        limit: PAGE_LIMIT,
      });
      setRows(res.data);
      setTruncated(res.nextCursor !== null);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [effectiveVenueId, statusParam, dateFromParam, dateToParam, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList();
  }, [fetchList]);

  function updateParam(key: string, value: string | null): void {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });
  }

  /**
   * BP-16 — validate before committing to the URL: a non-UUID value would
   * 422 server-side and blank the result list behind a generic error banner.
   */
  function commitVenueId(): void {
    const trimmed = venueIdInput.trim();
    if (trimmed && !UUID_RE.test(trimmed)) {
      setVenueIdError(
        t('booking:list.filter_venue_invalid', {
          defaultValue: 'Enter a full venue UUID (8-4-4-4-12 hex characters).',
        }),
      );
      return;
    }
    setVenueIdError(null);
    updateParam('venueId', trimmed || null);
  }

  function onDateRangeChange(range: DateRange): void {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (range.start) next.set('dateFrom', range.start);
      else next.delete('dateFrom');
      if (range.end) next.set('dateTo', range.end);
      else next.delete('dateTo');
      return next;
    });
  }

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('booking:filter.status_all', { defaultValue: 'Any status' }) },
      ...BOOKING_STATUS_VALUES.map((s) => ({
        value: s,
        label: t(`booking:status.${s}`, { defaultValue: STATUS_LABEL[s] }),
      })),
    ],
    [t],
  );

  const dateRange: DateRange = useMemo(
    () => ({ start: dateFromParam || undefined, end: dateToParam || undefined }),
    [dateFromParam, dateToParam],
  );

  const columns = useMemo<readonly TableColumn<BookingSummary>[]>(
    () => [
      {
        key: 'booking',
        header: t('booking:list.column_booking', { defaultValue: 'Booking' }),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span className="text-[13px] font-medium text-ink capitalize">{row.sportName}</span>
            <span className="font-mono text-[11px] text-ink-3">{row.id.slice(0, 8)}…</span>
          </div>
        ),
      },
      {
        key: 'venue',
        header: t('booking:list.column_venue', { defaultValue: 'Venue / court' }),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span className="text-[13px] text-ink">{row.venueName}</span>
            <span className="text-[11.5px] text-ink-3">{row.subVenueName}</span>
          </div>
        ),
      },
      {
        key: 'when',
        header: t('booking:list.column_when', { defaultValue: 'When' }),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-2">
            {formatSlot(row.startAt, row.venueTimezone, locale)}
          </span>
        ),
      },
      {
        key: 'status',
        header: t('booking:list.column_status', { defaultValue: 'Status' }),
        cell: (row) => (
          <Badge tone={STATUS_TONE[row.status]} size="sm">
            {t(`booking:status.${row.status}`, { defaultValue: STATUS_LABEL[row.status] })}
          </Badge>
        ),
      },
      {
        key: 'party',
        header: t('booking:list.column_party', { defaultValue: 'Party' }),
        align: 'end',
        cell: (row) => <span className="text-[12.5px] text-ink-2">{row.partySize}</span>,
      },
      {
        key: 'price',
        header: t('booking:list.column_price', { defaultValue: 'Price' }),
        align: 'end',
        cell: (row) => (
          <CurrencyDisplay amount={row.price.priceAmount} currency={row.price.priceCurrency} />
        ),
      },
    ],
    [t, locale],
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
      {/* Filter sidebar */}
      <Card>
        <div className="flex flex-col gap-3" data-testid="admin-bookings-filters">
          <h2 className="text-h2 font-bold text-ink">
            {t('booking:list.filters_title', { defaultValue: 'Filters' })}
          </h2>

          {!isLocked ? (
            <FormField
              label={t('booking:list.filter_venue', { defaultValue: 'Venue id' })}
              error={venueIdError ?? undefined}
            >
              <TextInput
                value={venueIdInput}
                onChange={(e) => {
                  setVenueIdInput(e.target.value);
                  if (venueIdError) setVenueIdError(null);
                }}
                onBlur={commitVenueId}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitVenueId();
                }}
                placeholder={t('booking:list.filter_venue_placeholder', {
                  defaultValue: 'venue-uuid',
                })}
                data-testid="admin-bookings-filter-venue"
              />
            </FormField>
          ) : null}

          <FormField label={t('booking:list.filter_status', { defaultValue: 'Status' })}>
            <Select
              value={statusParam}
              onChange={(e) => updateParam('status', e.target.value || null)}
              options={statusOptions}
              data-testid="admin-bookings-filter-status"
            />
          </FormField>

          <FormField
            label={t('booking:list.filter_date_range', { defaultValue: 'Start date range' })}
          >
            <DateRangePicker value={dateRange} onChange={onDateRangeChange} />
          </FormField>
        </div>
      </Card>

      {/* Results */}
      <div className="flex flex-col gap-3">
        {error ? <Banner tone="danger">{error}</Banner> : null}

        {loading ? (
          <div className="space-y-3" data-testid="admin-bookings-loading">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-14 w-full rounded-lg" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<UIIcon name="calendar" size={36} color="currentColor" />}
            title={t('booking:list.empty_title', {
              defaultValue: 'No bookings match these filters',
            })}
            description={t('booking:list.empty_body', {
              defaultValue: 'Try widening the date range or clearing filters.',
            })}
          />
        ) : (
          <>
            <Table
              data={rows as BookingSummary[]}
              columns={columns as TableColumn<BookingSummary>[]}
              rowKey={(row) => row.id}
              onRowPress={(row) => navigate(`/bookings/${row.id}`)}
            />
            {truncated ? (
              <p className="text-[12px] text-ink-3" data-testid="admin-bookings-truncated">
                {t('booking:list.truncated_note', {
                  count: PAGE_LIMIT,
                  defaultValue: `Showing the {count} most recent. Narrow the date range or status to see older bookings.`,
                })}
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

export default AdminBookingsList;
